import React, { useState, useEffect } from 'react';
import mlApi from '../services/mlApi';

const VisualizationTab = ({ datasetId, featureColumns, targetColumn }) => {
  const [plots, setPlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlots = async () => {
      if (!datasetId || !featureColumns || !targetColumn) {
        setError('Missing required data for visualization');
        setLoading(false);
        return;
      }

      try {
        const params = new URLSearchParams();
        featureColumns.forEach(column => {
          params.append('feature_columns', column);
        });
        params.append('target_column', targetColumn);
        
        const response = await mlApi.get(
          `/analytics/plots/${datasetId}?${params.toString()}`
        );
        setPlots(response.data);
        setError(null);
      } catch (err) {
        setError('Error fetching visualizations');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlots();
  }, [datasetId, featureColumns, targetColumn]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Target Distribution */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Target Variable Distribution</h3>
        <img 
          src={`data:image/png;base64,${plots.target_distribution}`}
          alt="Target Distribution"
          className="w-full"
        />
      </div>

      {/* Feature Distributions */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Feature Distributions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featureColumns.map(feature => (
            <div key={`dist_${feature}`} className="bg-white p-4 rounded-lg shadow">
              <h4 className="font-medium mb-2">{feature}</h4>
              <img 
                src={`data:image/png;base64,${plots[`dist_${feature}`]}`}
                alt={`Distribution of ${feature}`}
                className="w-full"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Feature vs Target */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Features vs Target</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {featureColumns.map(feature => (
            <div key={`target_${feature}`} className="bg-white p-4 rounded-lg shadow">
              <h4 className="font-medium mb-2">{feature} vs {targetColumn}</h4>
              <img 
                src={`data:image/png;base64,${plots[`target_${feature}`]}`}
                alt={`${feature} vs ${targetColumn}`}
                className="w-full"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Correlation Heatmap */}
      {plots.correlation && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Feature Correlation Heatmap</h3>
          <img 
            src={`data:image/png;base64,${plots.correlation}`}
            alt="Correlation Heatmap"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default VisualizationTab;